import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Droplet,
  FileText,
  Globe,
  Heart,
  Building,
  Lightbulb,
  Menu,
  MessageCircle,
  Phone,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import Screen from '../../demo/Screen';
import BrowserFrame from '../../mocks/BrowserFrame';
import { Leaf } from '../../mocks/Scenery';
import './Hra.css';

/* ---------- Small filled glyphs, as drawn in the mockup (decorative) ---------- */

function CrossMark() {
  return (
    <svg className="hra-site__cross" viewBox="0 0 28 28" aria-hidden="true" focusable="false">
      <path d="M10 2h8v8h8v8h-8v8h-8v-8H2v-8h8Z" fill="#1463f3" />
      <path d="M14 2h4v8h8v4H14Z" fill="#5fa1ff" />
    </svg>
  );
}

function HeartGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 21C6 16.6 2.5 13 2.5 8.8 2.5 5.9 4.8 3.7 7.6 3.7c1.8 0 3.4.9 4.4 2.3 1-1.4 2.6-2.3 4.4-2.3 2.8 0 5.1 2.2 5.1 5.1 0 4.2-3.5 7.8-9.5 12.2Z"
        fill="currentColor"
      />
    </svg>
  );
}

function PersonPlusGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="10" cy="7" r="4.2" fill="currentColor" />
      <path d="M2.5 20.5c0-4.4 3.4-7.6 7.5-7.6 1.6 0 3 .4 4.2 1.2v6.4Z" fill="currentColor" />
      <circle cx="18" cy="17.5" r="4.6" fill="currentColor" />
      <path d="M18 15.3v4.4M15.8 17.5h4.4" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function ShieldGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 2.2 20 5v6.4c0 5-3.4 8.7-8 10.4-4.6-1.7-8-5.4-8-10.4V5Z" fill="currentColor" />
      <path
        d="m8.4 12 2.5 2.5 4.8-5"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PeopleGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true" focusable="false">
      <circle cx="6.2" cy="9" r="2.8" fill="currentColor" opacity="0.75" />
      <circle cx="19.8" cy="9" r="2.8" fill="currentColor" opacity="0.75" />
      <path
        d="M1 18.6c0-3 2.3-5.2 5.2-5.2 1.3 0 2.4.4 3.3 1.1-1.3 1.3-2 3-2 4.9v.4H1Z"
        fill="currentColor"
        opacity="0.75"
      />
      <path
        d="M25 18.6c0-3-2.3-5.2-5.2-5.2-1.3 0-2.4.4-3.3 1.1 1.3 1.3 2 3 2 4.9v.4H25Z"
        fill="currentColor"
        opacity="0.75"
      />
      <circle cx="13" cy="7.6" r="3.6" fill="currentColor" />
      <path d="M6.6 20.4c0-3.8 2.9-6.6 6.4-6.6s6.4 2.8 6.4 6.6v.6H6.6Z" fill="currentColor" />
    </svg>
  );
}

const GOALS = [
  {
    key: 'routine',
    icon: HeartGlyph,
    title: 'Routine checkup',
    sub: 'General health and wellness',
  },
  {
    key: 'concern',
    icon: PersonPlusGlyph,
    title: 'A health concern',
    sub: 'Symptoms or specific conditions',
  },
  {
    key: 'preventive',
    icon: ShieldGlyph,
    title: 'Preventive screening',
    sub: 'Age, lifestyle or risk based',
  },
];

const AGES = [
  { key: 'below-18', label: 'Below 18' },
  { key: '18-30', label: '18 – 30' },
  { key: '31-45', label: '31 – 45' },
  { key: '46-60', label: '46 – 60' },
  { key: 'above-60', label: 'Above 60' },
];

const PACKAGES = {
  'Preventive Health Check': {
    text: 'A comprehensive package to assess your overall health and screen for common risk factors.',
    points: [
      'CBC, Lipid Profile, HbA1c and more',
      'Assess key health risks early',
      'Personalised report with insights',
    ],
  },
  'Comprehensive Health Package': {
    text: 'A deeper look at the concern you raised, with the wider panel of tests around it.',
    points: [
      'Full body and organ function tests',
      'Focused tests for your symptoms',
      'Personalised report with insights',
    ],
  },
  'Senior Citizen Health Check': {
    text: 'Age-appropriate screening for the heart, bones, kidneys and blood sugar.',
    points: [
      'Lipid, HbA1c, kidney and bone tests',
      'Screen for age-related risks early',
      'Personalised report with insights',
    ],
  },
};

const POINT_ICONS = [Droplet, Heart, FileText];

/*
 * The quiz's rule: a health concern gets the comprehensive package, a routine
 * or preventive goal gets the preventive check, and above 60 always gets the
 * senior check.
 */
export function recommendPackage(goal, age) {
  if (age === 'above-60') return 'Senior Citizen Health Check';
  if (goal === 'concern') return 'Comprehensive Health Package';
  return 'Preventive Health Check';
}

const CAPTIONS = [
  {
    n: 1,
    title: 'Visitor selects a goal',
    text: 'A simple entry point on your website or WhatsApp.',
  },
  {
    n: 2,
    title: 'Short, guided assessment',
    text: 'A few relevant questions to understand their needs.',
  },
  {
    n: 3,
    title: 'Recommended package',
    text: 'The right checkup, with clear inclusions and next steps.',
  },
];

/* Where the HRA lives: titles only (their sub-lines went 2026-10-24). */
const CHANNELS = [
  { key: 'website', icon: Globe, tone: 'blue', title: 'Website' },
  { key: 'whatsapp', icon: MessageCircle, tone: 'green', title: 'WhatsApp' },
  { key: 'journey', icon: Users, tone: 'purple', title: 'Your patient journey' },
];

function Caption({ n, title, text }) {
  return (
    <p className={`hra-caption hra-caption--${n}`}>
      <span className="hra-caption__num" aria-hidden="true">
        {n}
      </span>
      <span>
        <strong>
          <span className="sr-only">{`Step ${n}: `}</span>
          {title}
        </strong>
        <span>{text}</span>
      </span>
    </p>
  );
}

function FlowArrow({ className }) {
  return (
    <svg className={`hra-arrow ${className}`} viewBox="0 0 48 24" aria-hidden="true" focusable="false">
      <path d="M2 18C14 4 30 4 42 12" />
      <path d="M36 6l7 6-8 4" />
    </svg>
  );
}

/* Cropped from the mockup (decorative). The report pages' typo "MEALTH" is retouched to "HEALTH". */
const RUNNER_SRC = `${process.env.PUBLIC_URL}/demo/hra/runner.png`;
const REPORT_SRC = `${process.env.PUBLIC_URL}/demo/hra/report-pages.png`;

/* Until the visitor reaches the result, the third panel shows this sample. */
const SAMPLE_PACKAGE = 'Preventive Health Check';

export default function Hra() {
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState(null);
  const [age, setAge] = useState(null);

  const goalRefs = useRef({});
  const ageRefs = useRef({});
  const resultHeading = useRef(null);
  const pendingFocus = useRef(null);

  // Move focus after the render that enables or reveals the target.
  useEffect(() => {
    if (!pendingFocus.current) return;
    const target = pendingFocus.current();
    pendingFocus.current = null;
    if (target) target.focus();
  });

  function chooseGoal(key) {
    setGoal(key);
    setStep(2);
    pendingFocus.current = () => ageRefs.current[age || AGES[0].key];
  }

  function chooseAge(key) {
    setAge(key);
    if (step === 3) setStep(2);
  }

  function back() {
    setStep(1);
    pendingFocus.current = () => goalRefs.current[goal || GOALS[0].key];
  }

  function next() {
    if (!goal || !age) return;
    setStep(3);
    pendingFocus.current = () => resultHeading.current;
  }

  function restart() {
    setGoal(null);
    setAge(null);
    setStep(1);
    pendingFocus.current = () => goalRefs.current[GOALS[0].key];
  }

  // The result (step 3) is the real recommendation; before that the panel shows a sample.
  const isResult = step === 3;
  const packageName = isResult ? recommendPackage(goal, age) : SAMPLE_PACKAGE;
  const pkg = PACKAGES[packageName];
  // The mockup's first panel shows Routine checkup chosen; until a goal is picked it is highlighted as the suggestion.
  const shownGoal = goal || GOALS[0].key;
  // Likewise the age panel previews 31 – 45 while it waits for a goal (disabled, so not a real answer).
  const shownAge = step === 1 && !age ? '31-45' : age;

  return (
    <Screen id="product.hra" title="HRA" className="hra">
      <p className="eyebrow hra-eyebrow">HRA • Health risk assessment</p>
      <h1 className="headline hra-headline">
        Turn curious visitors into the <em>right checkup.</em>
      </h1>

      <div className="hra-board">
        <div className="hra-board__in">
          <div className="hra-quiz" data-trapezium-state={`step-${step}`}>
            <span className="hra-leaves" aria-hidden="true">
              <Leaf className="hra-leaf hra-leaf--a" />
              <Leaf className="hra-leaf hra-leaf--b" />
              <Leaf className="hra-leaf hra-leaf--c" />
              <Leaf className="hra-leaf hra-leaf--d" />
            </span>

            {/* Step 1: goal */}
            <section className="hra-panel hra-panel--1" aria-labelledby="hra-q1">
              <BrowserFrame url="yourlab.com" className="hra-browser">
                <div className="hra-site">
                  <div className="hra-site__bar" aria-hidden="true">
                    <span className="hra-site__logo">
                      <CrossMark />
                      CityCare Labs
                    </span>
                    <Menu size={22} strokeWidth={2} />
                  </div>
                  <h2 id="hra-q1" className="hra-q">
                    What would you like help with today?
                  </h2>
                  <p className="hra-site__intro">Take a short health assessment to find the right checkup for you.</p>
                  <div className="hra-goals" role="group" aria-labelledby="hra-q1">
                    {GOALS.map(({ key, icon: Icon, title, sub }) => {
                      const pressed = goal === key;
                      const highlighted = shownGoal === key;
                      return (
                        <button
                          key={key}
                          ref={(el) => {
                            goalRefs.current[key] = el;
                          }}
                          type="button"
                          className={`hra-goal${highlighted ? ' is-active' : ' is-dimmed'}`}
                          aria-pressed={pressed}
                          onClick={() => chooseGoal(key)}
                          data-trapezium-id={`product.hra.goal-${key}`}
                        >
                          <span className="hra-goal__icon" aria-hidden="true">
                            <Icon />
                          </span>
                          <span className="hra-goal__text">
                            <strong>{title}</strong>
                            <span>{sub}</span>
                          </span>
                          <ChevronRight className="hra-goal__chev" size={16} strokeWidth={2.4} aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </BrowserFrame>
            </section>
            <Caption {...CAPTIONS[0]} />

            <FlowArrow className="hra-arrow--1" />

            {/* Step 2: age group */}
            <section className="hra-panel hra-panel--2 hra-screen" aria-label="Assessment question">
              <div className="hra-screen__top" aria-hidden="true">
                <ChevronLeft size={20} strokeWidth={2.2} />
                <span>2 / 2</span>
              </div>
              <span className="hra-screen__progress" aria-hidden="true">
                <span style={{ width: age ? '100%' : '30%' }} />
              </span>
              <fieldset className="hra-ages" disabled={step === 1}>
                <legend className="hra-q hra-q--small">What is your age group?</legend>
                <div className="hra-ages__body">
                  <div className="hra-ages__list">
                    {AGES.map((a) => (
                      <label
                        key={a.key}
                        className={`hra-age${shownAge === a.key ? ' is-checked' : shownAge ? ' is-dimmed' : ''}`}
                      >
                        <span>{a.label}</span>
                        <input
                          ref={(el) => {
                            ageRefs.current[a.key] = el;
                          }}
                          type="radio"
                          name="hra-age"
                          value={a.key}
                          checked={age === a.key}
                          onChange={() => chooseAge(a.key)}
                          data-trapezium-id={`product.hra.age-${a.key}`}
                        />
                        <span className="hra-age__tick" aria-hidden="true" />
                      </label>
                    ))}
                  </div>
                  <div className="hra-ages__aside">
                    <img className="hra-runner" src={RUNNER_SRC} alt="" width="134" height="132" />
                    <p className="hra-why">
                      <strong>
                        <Lightbulb size={16} strokeWidth={2.2} fill="currentColor" aria-hidden="true" />
                        Why we ask this?
                      </strong>
                      Age can influence the recommended tests for your health goals.
                    </p>
                  </div>
                </div>
              </fieldset>
              <div className="hra-screen__nav">
                <button
                  type="button"
                  className="hra-back"
                  onClick={back}
                  disabled={step === 1}
                  data-trapezium-id="product.hra.back"
                >
                  <ArrowLeft size={17} aria-hidden="true" />
                  Back
                </button>
                <button
                  type="button"
                  className={`btn btn--primary hra-next${step === 1 ? ' is-preview' : ''}`}
                  onClick={next}
                  disabled={step === 1 || !age}
                  data-trapezium-id="product.hra.next"
                >
                  Next
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              </div>
            </section>
            <Caption {...CAPTIONS[1]} />

            <FlowArrow className="hra-arrow--2" />

            {/* Step 3: result */}
            <section className="hra-panel hra-panel--3 hra-screen" aria-label="Recommendation">
              <p className="hra-result__eyebrow">
                <Sparkles size={20} strokeWidth={1.6} fill="currentColor" aria-hidden="true" />
                Recommended for you
              </p>
              <div
                className={`hra-result${isResult ? '' : ' hra-result--sample'}`}
                data-trapezium-state={isResult ? 'recommended' : 'sample'}
              >
                <h2 className="hra-result__name" tabIndex={-1} ref={resultHeading}>
                  {packageName}
                </h2>
                <p className="hra-result__text">{pkg.text}</p>
                <ul className="hra-result__points">
                  {pkg.points.map((point, i) => {
                    const Icon = POINT_ICONS[i];
                    return (
                      <li key={point}>
                        <span className="hra-result__point-icon" aria-hidden="true">
                          <Icon
                            size={13}
                            strokeWidth={i === 2 ? 2 : 2.4}
                            fill="#e5484d"
                            color={i === 2 ? '#fff' : '#e5484d'}
                          />
                        </span>
                        {point}
                      </li>
                    );
                  })}
                </ul>
                <img className="hra-report" src={REPORT_SRC} alt="" width="306" height="188" />
                <Link
                  to="/packages"
                  className="btn btn--primary hra-explore"
                  data-trapezium-id="product.hra.explore-package"
                >
                  Explore package
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
                {isResult && (
                  <button
                    type="button"
                    className="btn btn--ghost hra-restart"
                    onClick={restart}
                    data-trapezium-id="product.hra.restart"
                  >
                    <RotateCcw size={14} aria-hidden="true" />
                    Restart
                  </button>
                )}
              </div>
            </section>
            <Caption {...CAPTIONS[2]} />
          </div>

          <aside className="hra-config" aria-labelledby="hra-config-title">
            <span className="hra-config__tile" aria-hidden="true">
              <Building size={30} strokeWidth={1.7} />
            </span>
            <h2 id="hra-config-title" className="hra-config__title">
              Configured for your lab. Ready for your website or WhatsApp.
            </h2>
            <ul className="hra-channels">
              {CHANNELS.map(({ key, icon: Icon, tone, title }) => (
                <li key={key}>
                  <span className={`hra-channels__icon is-${tone}`} aria-hidden="true">
                    {key === 'whatsapp' ? (
                      <span className="hra-wa">
                        <MessageCircle size={28} strokeWidth={2.2} />
                        <Phone size={12} strokeWidth={0} fill="currentColor" />
                      </span>
                    ) : key === 'journey' ? (
                      <PeopleGlyph />
                    ) : (
                      <Icon size={30} strokeWidth={1.7} />
                    )}
                  </span>
                  <strong>{title}</strong>
                </li>
              ))}
            </ul>
            <p className="hra-config__foot">
              <span className="hra-config__shield" aria-hidden="true">
                <ShieldCheck size={22} strokeWidth={1.8} />
              </span>
              Customised with your team • Designed to guide, not diagnose
            </p>
          </aside>
        </div>
      </div>
    </Screen>
  );
}
